import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogTitle} from "@mui/material";
import TopBarProgressIndicator from "../../app-specific/topbar-progress-indicator";
import SelectItemsSection from "./sections/select-items";
import LoadingSection from "./sections/loading";
import ResultsSection from "./sections/results";

/**
 * The different types of the quick order entities
 */
const issueCreditFromInvoiceSteps = {
    selectItems: 'Select products',
    loading: 'Credit Issuing',
    results: 'Results',
}


const IssueCreditFromInvoiceDialog = ({items: itemsProps, open, close, invoiceNo, customerId}) => {
    const [status, setStatus] = useState(issueCreditFromInvoiceSteps.selectItems);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [order, setOrder] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Fetches partner Spire warehouses (v2) for credit warehouse assignment.
     * @type {(function(): Promise<void>)}
     */
    const fetchWarehouses = useCallback(async () => {
        const response = await Api.readAllWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses((response.data ?? []).map((warehouse) => ({
                ...warehouse,
                name: warehouse.title ?? warehouse.name,
                title: warehouse.title ?? warehouse.name,
            })));
        }
    }, [isMounted])

    /**
     * Fetches the warehouses as soon as the component mounts.
     */
    useEffect(() => {
        if (!open)
            return;
        void fetchWarehouses();
    }, [fetchWarehouses, open])

    /**
     * As soon as the dialog is opened, the status is set to the first step. The items are set to the props.
     */
    useLayoutEffect(() => {
        if (open) {
            setStatus(issueCreditFromInvoiceSteps.selectItems);
            setItems(itemsProps?.map(item => ({
                id: item.id,
                partNo: item.partNo,
                warehouseCode: item.whse,
                quantity: Number(item.committedQty),
                unitPrice: Number(item.unitPrice),
                serials: item.serials?.map(serial => serial.serialNumber) ?? [],
                committedQuantity: Number(item.committedQty),
                isSerialized: item.isSerialized,
                typeInfo: item.typeInfo,
            })) ?? []);
            setSelectedItems([]);
            setOrder(null);
        }
    }, [itemsProps, open])

    const visibleSteps = useMemo(() => [
        issueCreditFromInvoiceSteps.selectItems,
        issueCreditFromInvoiceSteps.loading,
        issueCreditFromInvoiceSteps.results,
    ], []);

    /**
     * Determines if a ball has been passed over relative to their process of adding multi invoices
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case issueCreditFromInvoiceSteps.selectItems:
            default:
                return false;
            case issueCreditFromInvoiceSteps.loading:
                return (type === issueCreditFromInvoiceSteps.selectItems);
            case issueCreditFromInvoiceSteps.results:
                return (type === issueCreditFromInvoiceSteps.selectItems || type === issueCreditFromInvoiceSteps.loading);
        }
    }, [])

    /**
     * Sets the state of the dialog to loading to call the necessary API calls to create the credit.
     * @type {(function(*): void)}
     */
    const onItemsSelected = useCallback((selectedItems) => {
        setSelectedItems(selectedItems);
        setStatus(issueCreditFromInvoiceSteps.loading);
    }, [])

    /**
     * Sets the order that was created from the credit.
     * @type {(function(*): void)}
     */
    const onOrderCreated = useCallback((order) => {
        setOrder(order);
        setStatus(issueCreditFromInvoiceSteps.results);
    }, [])


    const dialogSize = useMemo(() => {
        switch (status) {
            case issueCreditFromInvoiceSteps.selectItems:
            default:
                return 'lg';
            case issueCreditFromInvoiceSteps.loading:
                return 'sm';
            case issueCreditFromInvoiceSteps.results:
                return 'md';
        }
    }, [status])

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={dialogSize}
            loading={status === issueCreditFromInvoiceSteps.loading}
            disableBackdropClick={status === issueCreditFromInvoiceSteps.loading}
            disableEscapeKeyDown={status === issueCreditFromInvoiceSteps.loading}
            className={'bound-dialog'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle>
                <TopBarProgressIndicator
                    currentType={status}
                    types={visibleSteps}
                    passed={passed}
                />
            </DialogTitle>
            {
                status === issueCreditFromInvoiceSteps.selectItems &&
                <SelectItemsSection
                    items={items}
                    setItems={setItems}
                    selectedItems={selectedItems}
                    onNextStep={onItemsSelected}
                    onPreviousStep={close}
                    warehouses={warehouses}
                />
            }
            {
                status === issueCreditFromInvoiceSteps.loading &&
                <LoadingSection
                    selectedItems={selectedItems}
                    customerId={customerId}
                    invoiceNo={invoiceNo}
                    onPreviousStep={() => setStatus(issueCreditFromInvoiceSteps.selectItems)}
                    onNextStep={onOrderCreated}
                />
            }
            {
                status === issueCreditFromInvoiceSteps.results &&
                <ResultsSection
                    order={order}
                    close={close}
                    invoiceNo={invoiceNo}
                    warehouses={warehouses}
                />
            }
        </Dialog>
    );
}

export default IssueCreditFromInvoiceDialog;